import { TZDate } from '@date-fns/tz';
import type { InterpolationValues, Translation } from '@shared/translation';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subHours,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import type { dateRangeTexts } from '../texts';
import type { Preset, PresetCreateContext } from '../types';

const HOUR = 60 * 60;
const DAY = 24 * HOUR;

export type DateRangePresetTexts = typeof dateRangeTexts.presets;

export interface PresetDefinition extends Preset {
  getLabel: (texts: DateRangePresetTexts) => Translation;
  labelValues?: InterpolationValues;
}

export const builtInPresetDefinitions = [
  {
    id: 'last2hours',
    getLabel: (texts) => texts.lastXHours.label,
    labelValues: { numberOfHours: 2 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: subHours(now, 2),
        to: now,
      };
    },
    maxDurationSeconds: 2 * HOUR,
  },
  {
    id: 'last6hours',
    getLabel: (texts) => texts.lastXHours.label,
    labelValues: { numberOfHours: 6 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: subHours(now, 6),
        to: now,
      };
    },
    maxDurationSeconds: 6 * HOUR,
  },
  {
    id: 'last12hours',
    getLabel: (texts) => texts.lastXHours.label,
    labelValues: { numberOfHours: 12 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: subHours(now, 12),
        to: now,
      };
    },
    maxDurationSeconds: 12 * HOUR,
  },
  {
    id: 'last24hours',
    getLabel: (texts) => texts.lastXHours.label,
    labelValues: { numberOfHours: 24 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: subHours(now, 24),
        to: now,
      };
    },
    maxDurationSeconds: DAY,
  },
  {
    id: 'last7days',
    getLabel: (texts) => texts.lastXDays.label,
    labelValues: { numberOfDays: 7 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: startOfDay(subDays(now, 6)),
        to: endOfDay(now),
      };
    },
    maxDurationSeconds: 7 * DAY,
  },
  {
    id: 'last30days',
    getLabel: (texts) => texts.lastXDays.label,
    labelValues: { numberOfDays: 30 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: startOfDay(subDays(now, 29)),
        to: endOfDay(now),
      };
    },
    maxDurationSeconds: 30 * DAY,
  },
  {
    id: 'last90days',
    getLabel: (texts) => texts.lastXDays.label,
    labelValues: { numberOfDays: 90 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: startOfDay(subDays(now, 89)),
        to: endOfDay(now),
      };
    },
    maxDurationSeconds: 90 * DAY,
  },
  {
    id: 'today',
    getLabel: (texts) => texts.today.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: startOfDay(now),
        to: endOfDay(now),
      };
    },
    maxDurationSeconds: DAY,
  },
  {
    id: 'yesterday',
    getLabel: (texts) => texts.yesterday.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      const yesterday = subDays(now, 1);
      return {
        from: startOfDay(yesterday),
        to: endOfDay(yesterday),
      };
    },
    maxDurationSeconds: DAY,
  },
  {
    id: 'week',
    getLabel: (texts) => texts.week.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      const weekStartsOn = context?.weekStartsOn ?? 1;
      return {
        from: startOfWeek(now, { weekStartsOn }),
        to: endOfWeek(now, { weekStartsOn }),
      };
    },
    maxDurationSeconds: 7 * DAY,
  },
  {
    id: 'prevWeek',
    getLabel: (texts) => texts.previousWeek.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      const date = subWeeks(now, 1);
      const weekStartsOn = context?.weekStartsOn ?? 1;
      return {
        from: startOfWeek(date, { weekStartsOn }),
        to: endOfWeek(date, { weekStartsOn }),
      };
    },
    maxDurationSeconds: 7 * DAY,
  },
  {
    id: 'month',
    getLabel: (texts) => texts.month.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return { from: startOfMonth(now), to: endOfMonth(now) };
    },
    maxDurationSeconds: 31 * DAY,
  },
  {
    id: 'prevMonth',
    getLabel: (texts) => texts.previousMonth.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      const date = subMonths(now, 1);
      return {
        from: startOfMonth(date),
        to: endOfMonth(date),
      };
    },
    maxDurationSeconds: 31 * DAY,
  },
  {
    id: 'year',
    getLabel: (texts) => texts.year.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return { from: startOfYear(now), to: endOfYear(now) };
    },
    maxDurationSeconds: 365 * DAY,
  },
  {
    id: 'prevYear',
    getLabel: (texts) => texts.previousYear.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      const date = subYears(now, 1);
      return {
        from: startOfYear(date),
        to: endOfYear(date),
      };
    },
    maxDurationSeconds: 365 * DAY,
  },
] as const satisfies readonly PresetDefinition[];

export type BuiltInPresetId = (typeof builtInPresetDefinitions)[number]['id'];

function getNow(context?: PresetCreateContext): Date {
  const now = context?.referenceDate ?? new Date();
  return context?.timeZone ? new TZDate(now, context.timeZone) : now;
}
